import type { App, Item } from "@/services/fobizzToolsApi";
import { AppEnum } from "@/services/fobizzToolsApi";
import { RouterLinkProps } from "vue-router";

/** Query parameters for tool URLs */
export interface ToolQueryParams {
  outputKind?: "podcast" | "audio" | "text" | "lecture" | "transcript";
  [key: string]: string | undefined;
}

/** Base interface for all tools */
export interface BaseTool {
  id: AppEnum;
  namespace: string;
  name: string;
  clientPath: string;
  color?: string;
  icon?: string;
  infoUrl?: string;
  experimental?: boolean;
  new?: boolean;
  group?: string;
  supportsTemplates?: boolean;
  supportsAiGeneration?: boolean;
}

/** Frontend-defined tool definition */
export interface ToolDefinition extends BaseTool {
  source: "frontend";
  queryParams?: ToolQueryParams;
}

/** Backend-provided app with discriminant */
export interface BackendApp extends App {
  source: "backend";
}

/** Union type for all possible tool types with discriminant */
export type Tool = ToolDefinition | BackendApp;

/** Type guard to check if a tool is frontend-defined */
export function isFrontendTool(tool: Tool): tool is ToolDefinition {
  return tool.source === "frontend";
}

/** Type guard to check if a tool is backend-provided */
export function isBackendTool(tool: Tool): tool is BackendApp {
  return tool.source === "backend";
}

/** Type for menu item app data */
export type MenuItemAppData = Pick<Tool, "namespace" | "clientPath"> & {
  supportsTemplates?: boolean;
  supportsAiGeneration?: boolean;
};

/** Minimal shape for a Startpage section */
export interface StartPageSection {
  title: string;
  /** Optional badge label (like "experimental", "new", etc.) */
  badge?: string;
  tools: Tool[];
}

// Introduce a type for manual tool keys if you wish to be stricter. For now we use string.
export type ManualToolKey = string;

// Define a discriminated union for a tool order item.
export type ToolOrderItem =
  | { type: "manual"; key: ManualToolKey }
  | { type: "backend"; id: AppEnum };

/** Updated tool group configuration with unified ordering */
export interface ToolGroup {
  id: string;
  title: string;
  badge?: string;
  toolOrder: ToolOrderItem[];
}

/** Tool routing configuration */
export interface ToolRouting {
  getShowUrl: (
    appId: AppEnum,
    itemId: string
  ) => RouterLinkProps["to"] | undefined;
  getInfoUrl: (appId: AppEnum) => RouterLinkProps["to"] | undefined;
  getClientPath: (entity: App | ToolDefinition | Item) => string | undefined;
}
